function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

/**
 * Persists the booking after Stripe/subscription success. Retries transient failures so a brief
 * API blip does not block the success redirect.
 *
 * @param {string} accessToken - Supabase session access_token
 * @param {Record<string, unknown>} payload - booking_payload from create-subscription response
 * @param {{ maxAttempts?: number, sleepFn?: (ms: number) => Promise<void> }} [options]
 */
export async function persistBookingWithRetry(accessToken, payload, options = {}) {
  const maxAttempts = options.maxAttempts ?? 3;
  const sleepFn = options.sleepFn ?? sleep;

  let lastErr = 'Could not save your trip';
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const bookingRes = await fetch('/api/admin/bookings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });
      let bookingData;
      try {
        bookingData = await bookingRes.json();
      } catch {
        lastErr = 'Invalid response while saving your booking';
        if (attempt < maxAttempts && (bookingRes.status >= 500 || bookingRes.status === 429)) {
          await sleepFn(250 * 2 ** (attempt - 1));
          continue;
        }
        return { ok: false, error: lastErr };
      }
      const created = bookingData?.data;
      if (bookingRes.ok && created && !bookingData.error) {
        return { ok: true, bookingId: created.id };
      }
      lastErr =
        typeof bookingData?.error === 'string'
          ? bookingData.error
          : `Could not save your trip (${bookingRes.status})`;
      const errStr = String(bookingData?.error || '');
      const looksLikeDuplicate = /duplicate|unique constraint/i.test(errStr);
      if (
        attempt < maxAttempts &&
        (bookingRes.status >= 500 || bookingRes.status === 429) &&
        !looksLikeDuplicate
      ) {
        await sleepFn(250 * 2 ** (attempt - 1));
        continue;
      }
      return { ok: false, error: lastErr };
    } catch (e) {
      lastErr = e instanceof Error ? e.message : String(e);
      if (attempt < maxAttempts) {
        await sleepFn(250 * 2 ** (attempt - 1));
        continue;
      }
      return { ok: false, error: lastErr };
    }
  }
  return { ok: false, error: lastErr };
}
